import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useLayoutEffect, useMemo, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Checklist, ChecklistItem, Hub} from 'yougile-model'
import {Checkbox} from '@components/common/base/checkbox'
import {DragControl} from '@containers/hooks-draggable'
import {cn} from '@containers/html-utils'
import {useChecklistsStore} from '../../../checklists-store'
import {isPlaceholder} from '../../../placeholder'
import {ItemFullId} from '../../../types'
import {LinkSmall} from '../link-small'
import {useOnDrag} from '../useDrag'
import {ChecklistItemEdit} from './checklist-item-edit'
import {
  ChecklistItemContext,
  ChecklistItemStore,
  useChecklistItemStore
} from './checklist-item-store'

type Props = {
  task: Hub
  list: Checklist
  item: ChecklistItem
  checklistItemDragCtl: DragControl<ItemFullId>
}

const TwBorderContainer = tw.div<{$dragged: boolean}>`
  rounded-4

  ${p => (p.$dragged ? 'shadow-border-1 shadow-grey-l60 bg-grey-l100' : '')}
`

const TwContainer = tw.div`
  p-8
  cursor-default
  flex
  flex-col
  items-start
  justify-start
  rounded-4
`

const TwContentContainer = tw.div`
  w-full
`
const TwContent = tw.div`
  relative
  w-full
`
const TwLabel = tw.div<{$completed: boolean}>`
  text-xs-regular
  break-words
  pl-24
  whitespace-pre-wrap
  ${p => (p.$completed ? 'text-secondary' : 'text-primary')}
`
const TwButtonsContainer = tw.div`
  w-full
  flex
  gap-16
  items-center
  justify-start
  mt-16
  pl-24
  text-xs-regular
  text-secondary
`
const TwText = tw.div`
  text-xs-regular
`

const ChecklistItemControlBase = observer((props: Props) => {
  const {task, list, item, checklistItemDragCtl} = props
  const [state, store] = useChecklistItemStore()

  useLayoutEffect(() => {
    store.syncOnNew(task, list, item)
  }, [task, list])

  const itemId = useMemo(
    () => ({itemId: item.id, listId: list.id}),
    [list.id, item.id]
  )

  const handlers = useOnDrag(itemId, checklistItemDragCtl)

  if (state.editing) {
    return <ChecklistItemEdit item={item} />
  }

  const anyDragged = !!checklistItemDragCtl.draggedId?.itemId
  const dragged = checklistItemDragCtl.draggedId?.itemId === item.id
  const shouldBeHidden = dragged && !isPlaceholder(item)
  const labelId = shouldBeHidden ? undefined : `chat-checklist-item-${item.id}`

  return (
    <div
      className={cn(shouldBeHidden && 'hidden', anyDragged && 'custom-dragged')}
    >
      <div draggable {...handlers}>
        <TwBorderContainer $dragged={dragged}>
          <TwContainer
            className={cn(dragged && 'opacity-20', 'custom-dragged__hovered')}
          >
            <TwContentContainer onClick={store.startEditing}>
              <TwContent>
                <Checkbox
                  className="absolute left-0 top-0"
                  value={item.isCompleted}
                  onChange={store.toggleItemCompleted}
                  stopPropagation
                />
                <TwLabel id={labelId} $completed={item.isCompleted}>
                  {item.title}
                </TwLabel>
              </TwContent>
            </TwContentContainer>

            {state.unsavedTitle && !anyDragged && (
              <TwButtonsContainer>
                <TwText>{Lang.msg.editNotSaved1}</TwText>

                <LinkSmall color="primary" onMouseDown={store.startEditing}>
                  {Lang.msg.editSee}
                </LinkSmall>

                <LinkSmall
                  color="error"
                  onMouseDown={store.cancelEditing}
                  className="ml-auto"
                >
                  {Lang.msg.editDiscard}
                </LinkSmall>
              </TwButtonsContainer>
            )}
          </TwContainer>
        </TwBorderContainer>
      </div>
    </div>
  )
})

export const ChecklistItemControl = (props: Props) => {
  const [, checklistsStore] = useChecklistsStore()
  const [checklistItemStore] = useState(
    () => new ChecklistItemStore(checklistsStore)
  )

  return (
    <ChecklistItemContext.Provider value={checklistItemStore}>
      <ChecklistItemControlBase {...props} />
    </ChecklistItemContext.Provider>
  )
}
